import { compareDisplayNames } from '@utils/string.util';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { TenantViewModel } from '../device-list.component';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import {
  MatDateRangePicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { addYears, subDays } from 'date-fns';
import { DeviceTypeViewModel } from '@components/device-type-list/device-type-list-table/device-type-list-table.component';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ja } from 'date-fns/locale';
import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';

/** ダイアログオプション */
export type DialogOption = {
  updating: boolean;
  tenantList?: TenantViewModel[];
  deviceTypeList?: DeviceTypeViewModel[];
  target?: DeviceViewModel;
};

/** デバイスのViewModel */
export type DeviceViewModel = {
  /** シリアル番号 */
  serialNo: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** デバイス種類表示名 */
  deviceTypeDisplayName: string;
  /** テナントID */
  tenantId: string;
  /**LPN ID */
  lpnId: string;

  /** 契約開始日 */
  contractStartedAt: Date;
  /** 契約開始日 */
  contractExpiredAt: Date;
};

/** フォームグループ */
type DeviceFormGroup = FormGroup<{
  /** シリアル番号 */
  serialNo: FormControl<string>;

  /** デバイス種類表示名 */
  deviceTypeDisplayName: FormControl<string>;

  /** テナントID */
  tenantId: FormControl<string>;

  /**LPN ID */
  lpnId: FormControl<string>;


  /** 契約開始日 */
  contractStartedAt: FormControl<Date>;

  /** 契約開始日 */
  contractExpiredAt: FormControl<Date>;
}>;

/** 日付絞り込みのViewModel */
export type ContractFormViewModel = {
  /** 契約開始日付 */
  contractStartedAt: string;
  /** 契約終了日付 */
  contractExpiredAt: string;
};

@Component({
  selector: 'app-device-edit-dialog',
  standalone: true,
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: ja,
    },
    provideDateFnsAdapter({
      parse: {
        dateInput: 'yyyy/MM/dd',
      },
      display: {
        dateInput: 'yyyy/MM/dd',
        monthYearLabel: 'yyyy/MM',
        dateA11yLabel: 'yyyy/MM/dd',
        monthYearA11yLabel: 'yyyy/MM',
      },
    }),
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatDateRangePicker,
  ],
  templateUrl: './device-edit-dialog.component.html',
  styleUrl: './device-edit-dialog.component.scss',
})
export class DeviceEditDialogComponent {
  @ViewChild('deviceInput') deviceInput:
    | ElementRef<HTMLInputElement>
    | undefined;

  /** 更新中 */
  public updating: boolean;

  /** テナント一覧 */
  public tenantList: TenantViewModel[];

  /** デバイス種類一覧 */
  public deviceTypeList: DeviceTypeViewModel[];

  /** フォームグループ */
  public formGroup: DeviceFormGroup;

  /** 検索フォーム */
  contractFormGroup = new FormGroup({
    contractStartedAt: new FormControl(subDays(addYears(new Date(), 1), 1)),
    contractExpiredAt: new FormControl(new Date()),
  });

  // contractFormGroupの値をcontractFormViewModelに変換する
  public contractFormValue = this.contractFormGroup.value;
  contractFormViewModel: ContractFormViewModel = {
    contractStartedAt: this.contractFormValue.contractStartedAt!.toISOString(),
    contractExpiredAt: this.contractFormValue.contractExpiredAt!.toISOString(),
  };

  private updateContractFormViewModel() {
    const contractStartedAt = this.contractFormGroup.value.contractStartedAt;
    const contractExpiredAt = this.contractFormGroup.value.contractExpiredAt;

    if (contractStartedAt && contractExpiredAt) {
      this.contractFormViewModel = {
        contractStartedAt: contractStartedAt.toISOString(),
        contractExpiredAt: contractExpiredAt.toISOString(),
      };
    }
  }

  private dateRangeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const start = group.get('contractStartedAt')?.value;
    const end = group.get('contractExpiredAt')?.value;
    return start && end && start > end ? { dateRange: true } : null;
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) options: DialogOption,
    private dialogRef: MatDialogRef<DeviceEditDialogComponent>,
    private dialog: MatDialog,
  ) {
    this.contractFormGroup.valueChanges.subscribe(() => {
      this.updateContractFormViewModel();
    });
    this.updating = options.updating;
    this.tenantList = options.tenantList || [];
    this.deviceTypeList = (options.deviceTypeList || []).slice().sort((a, b) =>
      compareDisplayNames(a.displayName, b.displayName),
    );
    const contractStartedAt = options.target?.contractStartedAt
      ? new Date(options.target.contractStartedAt)
      : new Date();
    const contractExpiredAt = options.target?.contractExpiredAt
      ? new Date(options.target.contractExpiredAt)
      : subDays(addYears(new Date(), 1), 1);
    this.formGroup = new FormGroup({
      serialNo: new FormControl(options.target?.serialNo || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(128)],
      }),
      deviceTypeDisplayName: new FormControl(
        options.target?.deviceTypeDisplayName || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      tenantId: new FormControl(
        {
          value: options.target?.tenantId || '',
          disabled: this.updating, // mat-selectはHTML側ではdisabledにできないため
        },
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      lpnId: new FormControl(options.target?.lpnId || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(128)],
      }),
      contractStartedAt: new FormControl(contractStartedAt, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      contractExpiredAt: new FormControl(contractExpiredAt, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
    this.contractFormGroup = new FormGroup(
      {
        contractStartedAt: new FormControl(contractStartedAt, {
          validators: [Validators.required],
        }),
        contractExpiredAt: new FormControl(contractExpiredAt, {
          validators: [Validators.required],
        }),
      },
      { validators: this.dateRangeValidator },
    );
  }

  /**
   * 送信
   */
  public async submit() {
    const serialNo = this.formGroup.get('serialNo')?.value || '';
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `デバイス「${serialNo}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const displayName = this.formGroup.get('deviceTypeDisplayName')?.value || '';
    const selectedDeviceType = this.deviceTypeList.find(
      (deviceType) => deviceType.displayName === displayName,
    );
    const device: DeviceViewModel = {
      serialNo: this.formGroup.get('serialNo')?.value || '',
      deviceTypeId: selectedDeviceType?.deviceTypeId || '',
      deviceTypeDisplayName: displayName,
      tenantId: this.formGroup.get('tenantId')?.value || '',
      lpnId: this.formGroup.get('lpnId')?.value || '',
      contractStartedAt:
        this.contractFormGroup.get('contractStartedAt')?.value || new Date(),
      contractExpiredAt:
        this.contractFormGroup.get('contractExpiredAt')?.value ||
        subDays(addYears(new Date(), 1), 1),
    };
    this.dialogRef.close(device);
  }
}
